import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import ModalBase from "./ModalBase";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  asyncDelete,
  asyncGetList,
  asyncSearchList,
} from "../../../utils/httpRequests";
import { useSelector } from "react-redux";
import { useAlertContext } from "../../../contexts/alert/AlertProvider";
import { TableColumn } from "react-data-table-component";
import { cloneDeep, isArray } from "lodash";
import { v4 } from "uuid";
import TableApp from "../tableapp/TableApp";
import { LuTrash } from "react-icons/lu";
import { LoadingButton } from "@mui/lab";

interface IModalDuplicateRow {
  open: boolean;
  fields: { value: string; label: string }[];
  apiCode: string;
  columns: TableColumn<any>[];
  onClose: () => void;
}

function ModalDuplicateRow({
  open,
  apiCode,
  fields,
  columns,
  onClose,
}: IModalDuplicateRow) {
  const token = useSelector((state: any) => state.auth.token);
  const { showAlert } = useAlertContext();
  const [loading, setLoading] = useState<boolean>(false);
  const [load, setLoad] = useState<number>(0);
  const [data, setData] = useState<any[]>([]);
  const [isGlobal, setIsGlobal] = useState<boolean>(false);
  const [loadingDelete, setLoadingDelete] = useState<boolean>(false);

  const handleCheckDuplicate = async () => {
    if (!fields || loading) return;
    try {
      setLoading(true);
      const group: { [key: string]: any } = {};
      fields.forEach((item) => {
        group[item.value] = `$${item.value}`;
      });
      const query = {
        group: {
          _id: group,
          count: {
            $sum: 1,
          },
          documents: {
            $push: "$_id",
          },
        },
        match: { count: { $gt: 1 } },
      };
      const condition = {
        page: 1,
        limit: 10,
        ...query,
      };
      const resp = await asyncGetList({
        apiCode,
        token,
        condition: condition,
      });
      if (resp?.status === 200) {
        const dataClone = cloneDeep(resp.data);
        if (isArray(dataClone) && dataClone.length > 0) {
          for (let i = 0; i < dataClone.length; i++) {
            const respDocuments = await asyncSearchList({
              apiCode,
              token,
              condition: {
                page: 1,
                limit: 99999,
                q: { _id: { $in: dataClone[i].documents } },
              },
            });
            if (respDocuments.status === 200) {
              dataClone[i].documents = respDocuments.data;
            } else {
              dataClone[i].documents = [];
            }
          }
          setData(dataClone);
        }
      }
    } catch (error: any) {
      showAlert({
        type: "error",
        message:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Lỗi khi kiểm tra trùng dữ liệu",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAll = async () => {
    try {
      setLoadingDelete(true);
      for (let i = 0; i < data.length; i++) {
        const documents = data[i].documents || [];
        for (let j = 0; j < documents.length; j++) {
          if (documents[j].selected) {
            await asyncDelete({
              apiCode,
              token,
              id: documents[j]._id,
            });
          }
        }
      }
    } catch (error: any) {
      showAlert({
        type: "error",
        message:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Lỗi khi xóa dữ liệu",
      });
    } finally {
      setIsGlobal(false);
      setLoadingDelete(false);
      setLoad(load + 1);
    }
  };

  const handleCheckAll = (
    e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    const dataClone = cloneDeep(data);
    for (let i = 0; i < dataClone.length; i++) {
      const documents = dataClone[i].documents;
      for (let j = 1; j < documents.length; j++) {
        documents[j].selected = checked;
      }
    }
    setData(dataClone);
    setIsGlobal(checked);
  };

  useEffect(() => {
    if (open && apiCode) {
      handleCheckDuplicate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiCode, load]);

  return (
    <ModalBase
      open={open}
      handleClose={onClose}
      width="1200px"
      renderHeader={() => {
        return (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            sx={{ px: 2 }}
          >
            <Typography sx={{ fontSize: "20px" }}>
              Kết quả kiểm tra trùng
            </Typography>
            {data?.length > 0 && (
              <Stack direction="row" alignItems="center" gap={2}>
                {isGlobal && (
                  <Tooltip placement="bottom" title="Xóa các dòng trùng" arrow>
                    <LoadingButton
                      size="small"
                      variant="contained"
                      color="error"
                      sx={{
                        boxShadow: "none",
                        textTransform: "none",
                        "&:hover": { boxShadow: "none" },
                      }}
                      loading={loadingDelete}
                      onClick={() =>
                        showAlert({
                          type: "info",
                          onConfirm: handleDeleteAll,
                          title: "Xác nhận",
                          renderContent: () => {
                            return (
                              <Stack alignItems="center">
                                <Typography variant="h6">
                                  Xóa tất cả các dòng trùng dữ liệu
                                </Typography>
                                <Typography
                                  color="text.primary"
                                  sx={{
                                    textAlign: "center",
                                    fontStyle: "italic",
                                  }}
                                >
                                  Ghi chú:{" "}
                                  <Typography component="span" color="primary">
                                    'Dòng trùng dữ liệu'
                                  </Typography>{" "}
                                  là các dòng giống nhau ở tất cả các trường dữ
                                  liệu và được{" "}
                                  <Typography component="span" color="error">
                                    tô đỏ
                                  </Typography>{" "}
                                  để đánh dấu
                                </Typography>
                              </Stack>
                            );
                          },
                        })
                      }
                      startIcon={<LuTrash size={16} />}
                    >
                      Xóa
                    </LoadingButton>
                  </Tooltip>
                )}
                <FormControlLabel
                  label="Chọn tất cả"
                  control={
                    <Checkbox
                      size="small"
                      checked={isGlobal}
                      onChange={handleCheckAll}
                    />
                  }
                />
              </Stack>
            )}
          </Stack>
        );
      }}
      actions={[
        <Button
          key={1}
          onClick={onClose}
          variant="contained"
          color="error"
          size="small"
          sx={{
            textTransform: "none",
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          }}
        >
          Đóng
        </Button>,
      ]}
      hideCloseIcon
    >
      <Box sx={{ maxHeight: "80vh", overflow: "auto", px: 2 }}>
        {loading ? (
          <Stack direction="row" alignItems="center" justifyContent="center">
            <CircularProgress />
          </Stack>
        ) : (
          <>
            {isArray(data) && data.length > 0 ? (
              <Stack gap={2}>
                {data.map((row) => {
                  return (
                    <Displaydata
                      key={v4()}
                      row={row}
                      columns={columns}
                      apiCode={apiCode}
                      isGlobal={isGlobal}
                      setLoad={setLoad}
                    />
                  );
                })}
              </Stack>
            ) : (
              <Typography sx={{ textAlign: "center" }}>
                Không có kết quả trùng nào
              </Typography>
            )}
          </>
        )}
      </Box>
    </ModalBase>
  );
}

export default ModalDuplicateRow;

function Displaydata({
  row,
  columns,
  apiCode,
  isGlobal,
  setLoad,
}: {
  row: any;
  columns: TableColumn<any>[];
  apiCode: string;
  isGlobal: boolean;
  setLoad: Dispatch<SetStateAction<number>>;
}) {
  const theme = useTheme();
  const token = useSelector((state: any) => state.auth.token);
  const { showAlert } = useAlertContext();
  const [data, setData] = useState<any[]>(row.documents || []);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [toggleCleared, setToggleCleared] = useState<boolean>(false);

  const deleteSelectedRows = async () => {
    try {
      if (isArray(selectedRows) && selectedRows.length > 0) {
        for (let i = 0; i < selectedRows.length; i++) {
          const resp = await asyncDelete({
            apiCode,
            token,
            id: selectedRows[i]._id,
          });
          if (resp.status !== 200) {
            showAlert({
              type: "error",
              message: resp.data?.message || "Lỗi khi xóa dữ liệu",
            });
            break;
          }
          if (i === selectedRows.length - 1) {
            showAlert({
              type: "success",
              message: `Đã xóa thành công ${selectedRows.length} dòng`,
            });
            // loại bỏ các dòng đã xóa
            const ids = selectedRows.map((item) => item._id);
            const dataClone = cloneDeep(data);
            const newData = dataClone.filter((item) => !ids.includes(item._id));
            setData(newData);
          }
        }
      }
    } catch (error: any) {
      showAlert({
        type: "error",
        message:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Lỗi khi xóa dữ liệu",
      });
    } finally {
      setSelectedRows([]);
      setToggleCleared(!toggleCleared);
      setLoad((prev) => prev + 1);
    }
  };

  // const getData = async () => {
  //   const resp = await asyncSearchList({
  //     apiCode,
  //     token,
  //     condition: { _id: { $in: row.documents } },
  //   });
  // };

  // useEffect(() => {
  //   getData();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  return (
    <Stack>
      <Stack
        direction="row"
        alignItems="center"
        gap={2}
        sx={{ height: "32px" }}
      >
        <Typography
          sx={{
            color: "secondary.main",
            fontSize: "16px",
            fontWeight: 500,
          }}
        >
          {row.count} kết quả trùng
        </Typography>
        {selectedRows?.length > 0 && (
          <Stack direction="row" alignItems="center" gap={1}>
            <IconButton
              onClick={deleteSelectedRows}
              sx={{
                backgroundColor: "error.main",
                color: "common.white",
                borderRadius: "10px",
                "&:hover": {
                  backgroundColor: "error.main",
                  color: "common.white",
                },
              }}
            >
              <LuTrash size={16} />
            </IconButton>
            {/* <Button
              onClick={handleMerge}
              variant="contained"
              size="small"
              sx={{
                boxShadow: "none",
                textTransform: "none",
                fontWeight: 400,
                color: "common.white",
                "&:hover": { boxShadow: "none" },
              }}
            >
              Gộp
            </Button> */}
          </Stack>
        )}
      </Stack>
      <TableApp
        toggleCleared={toggleCleared}
        columns={columns}
        data={data || []}
        selectableRows={!isGlobal}
        onSelectedRowsChange={setSelectedRows}
        conditionalRowStyles={[
          {
            when: (row) => row.selected === true,
            style: {
              backgroundColor: `${theme.palette.error.main}33`,
              "&:hover": {
                backgroundColor: `${theme.palette.error.main}33`,
              },
            },
          },
        ]}
      />
    </Stack>
  );
}
